import PLAYER from '../playerData';
import { ITEMS, GENERATORS, instantiate } from '../data';
import { createLevel } from '../content/levels';

export class GameSimulation {
  constructor(levelNumber = 1, screenWidth = 390, screenHeight = 844) {
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;
    this.generatorCounter = 0;
    
    // Level setup
    this.currentLevel = levelNumber;
    this.levelData = createLevel(levelNumber);
    
    // Grid setup - always use level data
    this.positions = {};
    this.selectedItem = null;
    this.draggedItem = null;
    this.isWon = false;
    
    // Get board dimensions from level data
    this.boardWidth = this.levelData.dimensions.width || 5;
    this.boardHeight = this.levelData.dimensions.height || 5;
    
    // Grid position coordinates
    this.gridCoords = [];
    
    // Calculate center of the screen
    const centerX = this.screenWidth / 2;
    const centerY = this.screenHeight / 2;
    
    // Calculate grid dimensions
    const spacing = 60;
    const totalGridWidth = this.boardWidth * spacing;
    const totalGridHeight = this.boardHeight * spacing;
    
    // Calculate starting position to center the grid
    const startX = centerX - (totalGridWidth / 2) + (spacing / 2);
    const startY = centerY - (totalGridHeight / 2) + (spacing / 2);
    
    // Calculate grid positions based on board dimensions
    for (let row = 0; row < this.boardHeight; row++) {
      for (let col = 0; col < this.boardWidth; col++) {
        this.gridCoords.push({
          row,
          col,
          x: startX + col * spacing,
          y: startY + row * spacing
        });
      }
    }

    // Initialize board with generators in specified positions
    if (this.levelData.board && this.levelData.board.generators) {
      this.levelData.board.generators.forEach(generator => {
        const position = this.posToIndex(generator.position);
        if (position !== -1) {
          this.positions[position] = this.createGenerator(generator.generatorId, position);
        }
      });
    }
    
    // Initialize items if they exist in the level data
    if (this.levelData.items) {
      this.levelData.items.forEach(item => {
        if (item.itemId && item.position) {
          const position = this.posToIndex(item.position);
          if (position !== -1) {
            const coords = this.gridCoords[position];
            this.positions[position] = this.createItem(
              item.itemId, 
              position, 
              { x: coords.x, y: coords.y },
              item.frozen // Use the frozen property from the item
            );
          }
        }
      });
    }
    
    // Inventory panel state
    this.showInventory = false;
  }

  // Convert row/col position to index
  posToIndex(position) {
    if (!position || position.row === undefined || position.col === undefined) return -1;
    
    // Check if position is within board boundaries
    if (position.row < 0 || position.row >= this.boardHeight || 
        position.col < 0 || position.col >= this.boardWidth) {
      return -1;
    }
    
    return position.row * this.boardWidth + position.col;
  }
  
  // Convert index to row/col position
  indexToPos(index) {
    if (index < 0 || index >= this.boardWidth * this.boardHeight) return null;
    
    const row = Math.floor(index / this.boardWidth);
    const col = index % this.boardWidth;
    
    return { row, col };
  }

  getPlayerResources() {
    return PLAYER.resources;
  }

  getLevelObjective() {
    return this.levelData?.objective;
  }

  getCurrentProgress() {
    if (!this.levelData) return null;
    
    const objective = this.levelData.objective;
    let count = 0;
    
    Object.values(this.positions).forEach(item => {
      // Only count non-frozen items toward the objective
      if (item && !item.type && item.name === ITEMS[objective.itemId].name && !item.frozen) {
        count++;
      }
    });
    
    return {
      current: count,
      target: objective.count
    };
  }

  createGenerator(generatorId, position) {
    const generatorData = GENERATORS[generatorId];
    if (!generatorData) return null;

    const coords = this.gridCoords[position];

    return instantiate({
      ...generatorData,
      position,
      x: coords.x,
      y: coords.y,
      lastChargeTime: Date.now(),
      isRecharging: false,
      rechargeStartTime: 0,
      rechargeTime: 60000 // 60 seconds in milliseconds
    });
  }

  createItem(type, position, coords, frozen = false) {
    const itemData = ITEMS[type];
    return instantiate({
      level: Object.keys(ITEMS).indexOf(type) + 1,
      name: itemData.name,
      emoji: itemData.emoji,
      position,
      x: coords.x,
      y: coords.y,
      frozen: frozen // Add frozen property
    });
  }

  getNextLevelItem(level) {
    const item = Object.values(ITEMS).find(i => Object.keys(ITEMS).indexOf(i.id) === level);
    return item || null;
  }

  getTotalGeneratorCharges() {
    let total = 0;
    let max = 0;
    for (const item of Object.values(this.positions)) {
      if (item?.type === 'generator') {
        total += item.charges;
        max += item.maxCharges;
      }
    }
    return { current: total, max };
  }

  update(deltaTime) {
    // Update all generators
    Object.entries(this.positions).forEach(([pos, item]) => {
      if (item?.type === 'generator') {
        // Check if generator is recharging
        if (item.isRecharging) {
          const now = Date.now();
          const elapsedTime = now - item.rechargeStartTime;
          
          // If recharge time has passed, restore charges
          if (elapsedTime >= item.rechargeTime) {
            item.charges = item.maxCharges;
            item.isRecharging = false;
          }
        }
        
        // Remove non-infinite generator if empty and not recharging
        if (item.charges <= 0 && !item.infinite && !item.isRecharging) {
          // Start recharging
          item.isRecharging = true;
          item.rechargeStartTime = Date.now();
        }
        
        // Reset click state - reduced from 100ms to 50ms
        if (item.isClicked) {
          item.isClicked = false;
        }
      }
    });

    // Check win condition
    if (!this.isWon && this.levelData) {
      const progress = this.getCurrentProgress();
      if (progress.current >= progress.target) {
        this.isWon = true;
        // No longer adding level to completed levels or rewards here
        // This will be handled by App.jsx
      }
    }
  }

  getEmptyPosition() {
    const totalPositions = this.boardWidth * this.boardHeight;
    for (let i = 0; i < totalPositions; i++) {
      if (!this.positions[i]) return i;
    }
    return -1;
  }

  generateItem(generatorPosition) {
    const generator = this.positions[generatorPosition];
    if (!generator || generator.type !== 'generator') return;
    
    // Don't generate if recharging
    if (generator.isRecharging) return;
    
    // Check if infinite generator has enough energy
    if (generator.infinite && PLAYER.resources.energy < generator.energyCost) return;
    
    if (generator.charges <= 0 && !generator.infinite) return;
    
    const emptyPos = this.getEmptyPosition();
    if (emptyPos === -1) return;

    generator.isClicked = true;
    generator.clickTime = Date.now();

    // Calculate total weight
    const totalWeight = generator.generates.reduce((sum, item) => sum + item.weight, 0);
    
    // Random number between 0 and total weight
    let random = Math.random() * totalWeight;
    
    // Select item based on weights
    let selectedItem = generator.generates[0].itemId;
    for (const item of generator.generates) {
      random -= item.weight;
      if (random <= 0) {
        selectedItem = item.itemId;
        break;
      }
    }

    const coords = this.gridCoords[emptyPos];
    this.positions[emptyPos] = this.createItem(selectedItem, emptyPos, { x: coords.x, y: coords.y });
    
    // Deduct energy cost for infinite generators
    if (generator.infinite) {
      PLAYER.resources.energy -= generator.energyCost;
    }
    
    generator.charges--;
  }

  getRemainingRechargeTime(generator) {
    if (!generator || !generator.isRecharging) return 0;
    
    const now = Date.now();
    const elapsedTime = now - generator.rechargeStartTime;
    const remainingTime = Math.max(0, generator.rechargeTime - elapsedTime);
    
    return Math.ceil(remainingTime / 1000); // Return seconds remaining
  }

  selectItem(position) {
    const totalPositions = this.boardWidth * this.boardHeight;
    if (position < 0 || position >= totalPositions) return;
    
    const item = this.positions[position];
    if (!item) {
      this.selectedItem = null;
      return;
    }

    if (item.type === 'generator') {
      // Don't generate if recharging
      if (!item.isRecharging) {
        this.generateItem(position);
      }
      this.selectedItem = null;
      return;
    }

    // Don't select frozen items
    if (item.frozen) {
      // If we already have a selected item, try to merge with this frozen item
      if (this.selectedItem && !this.selectedItem.frozen && this.selectedItem.level === item.level) {
        this.mergeItems(this.selectedItem.position, position);
      }
      this.selectedItem = null;
      return;
    }

    if (!this.selectedItem) {
      this.selectedItem = item;
    } else {
      if (this.selectedItem.level === item.level && this.selectedItem !== item) {
        this.mergeItems(this.selectedItem.position, position);
      }
      this.selectedItem = null;
    }
  }

  mergeItems(pos1, pos2) {
    const item1 = this.positions[pos1];
    const item2 = this.positions[pos2];
    
    if (!item1 || !item2 || item1.level !== item2.level) return;
    
    // Special case: if one item is frozen, the result should be unfrozen
    const nextItem = this.getNextLevelItem(item1.level);
    if (!nextItem) return;

    delete this.positions[pos1];
    
    const coords = this.gridCoords[pos2];
    this.positions[pos2] = this.createItem(
      Object.keys(ITEMS).find(key => ITEMS[key] === nextItem),
      pos2,
      { x: coords.x, y: coords.y },
      false // Result is always unfrozen
    );
  }

  startDrag(position) {
    const totalPositions = this.boardWidth * this.boardHeight;
    if (position < 0 || position >= totalPositions) return;
    
    const item = this.positions[position];
    if (!item || item.type === 'generator') return;
    
    // Don't allow dragging frozen items
    if (item.frozen) return;
    
    this.selectedItem = null;
    this.draggedItem = {
      ...item,
      originalPosition: position
    };
    delete this.positions[position];
  }

  updateDragPosition(x, y) {
    if (!this.draggedItem) return;
    this.draggedItem.x = x;
    this.draggedItem.y = y;
  }

  endDrag(x, y) {
    if (!this.draggedItem) return;

    let nearestPos = -1;
    let minDist = Infinity;
    let nearestMatchingItem = null;

    // First check for matching items to merge with (including frozen items)
    Object.entries(this.positions).forEach(([pos, item]) => {
      if (!item || item.type === 'generator' || item.level !== this.draggedItem.level) return;
      
      const coords = this.gridCoords[parseInt(pos)];
      const dx = x - coords.x;
      const dy = y - coords.y;
      const dist = dx * dx + dy * dy;
      
      if (dist < minDist) {
        minDist = dist;
        nearestPos = parseInt(pos);
        nearestMatchingItem = item;
      }
    });

    // If we found a matching item nearby, merge with it
    if (nearestMatchingItem && minDist < 900) {
      // Place dragged item in original position temporarily for merge
      this.positions[this.draggedItem.originalPosition] = this.draggedItem;
      this.mergeItems(this.draggedItem.originalPosition, nearestPos);
    } else {
      // Otherwise find nearest empty position
      minDist = Infinity;
      nearestPos = -1;
      
      const totalPositions = this.boardWidth * this.boardHeight;
      for (let i = 0; i < totalPositions; i++) {
        if (this.positions[i]) continue;
        
        const coords = this.gridCoords[i];
        const dx = x - coords.x;
        const dy = y - coords.y;
        const dist = dx * dx + dy * dy;
        
        if (dist < minDist) {
          minDist = dist;
          nearestPos = i;
        }
      }

      if (nearestPos !== -1 && minDist < 900) {
        this.draggedItem.position = nearestPos;
        const coords = this.gridCoords[nearestPos];
        this.draggedItem.x = coords.x;
        this.draggedItem.y = coords.y;
        this.positions[nearestPos] = this.draggedItem;
      } else {
        // Return to original position
        this.positions[this.draggedItem.originalPosition] = this.draggedItem;
        const coords = this.gridCoords[this.draggedItem.originalPosition];
        this.draggedItem.x = coords.x;
        this.draggedItem.y = coords.y;
      }
    }

    this.draggedItem = null;
  }

  cancelDrag() {
    if (!this.draggedItem) return;
    
    // Return item to original position
    this.positions[this.draggedItem.originalPosition] = this.draggedItem;
    const coords = this.gridCoords[this.draggedItem.originalPosition];
    this.draggedItem.x = coords.x;
    this.draggedItem.y = coords.y;
    
    this.draggedItem = null;
  }
  
  toggleInventory() {
    this.showInventory = !this.showInventory;
  }
  
  useInventoryItem(index) {
    if (index < 0 || index >= PLAYER.inventory.length) return;
    
    const item = PLAYER.inventory[index];
    
    // Handle generator items
    if (item.type === "generator" && item.generatorId) {
      const emptyPos = this.getEmptyPosition();
      if (emptyPos === -1) return false;
      
      this.positions[emptyPos] = this.createGenerator(item.generatorId, emptyPos);
      // Remove from inventory
      PLAYER.inventory.splice(index, 1);
      return true;
    }
    
    return false;
  }
}
